import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  ButtonDropdown,
  FormField,
  Input,
  InputProps,
  Modal,
  Popover,
  SpaceBetween,
  StatusIndicator,
} from "@amzn/awsui-components-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Investigation,
  InvestigationUpdateRequest,
  Stage,
} from "../../lusca-search-api/generated-src";
import {
  InvestigationApiError,
  investigationKeys,
  useClaimInvestigation,
  useReleaseInvestigation,
  useTransferInvestigation,
  useUpdateInvestigation,
} from "../../hooks/useInvestigations";
import {
  investigationPath,
  excludedValues,
  isExclusion,
  predicateValues,
  RULE_FIELDS,
} from "./investigationUtils";
import { saveErrorText } from "./versionConflict";
import { SafeExternalLink, safeExternalHref } from "./SafeExternalLink";

interface InvestigationTaskActionProps {
  stage: Stage;
  investigation: Investigation;
  currentUser?: string | null;
  onError: (message: string) => void;
}

const TASKEI_CREATE_URL = "https://taskei.amazon.dev/tasks/create";

/** Mirrors the accepted shapes in the backend's parse_task_reference (URL or bare id). */
const TASK_URL_RE =
  /^https:\/\/(?:taskei\.amazon\.dev\/tasks|(?:issues|sim)\.amazon\.com\/issues)\/[A-Za-z0-9][A-Za-z0-9_-]*\/?$/;
const TASK_ID_RE = /^[A-Za-z][A-Za-z0-9_]*-\d+$|^[0-9a-fA-F-]{36}$/;
export const isTaskReference = (value: string): boolean =>
  TASK_URL_RE.test(value.trim()) || TASK_ID_RE.test(value.trim());

/**
 * Prefilled Taskei "create task" URL for an investigation. Opened in a new tab; the person
 * creating the task is the Midway user, so no service identity or Maxis onboarding is needed.
 * Only the investigation's own metadata goes into the task (title, stage, rule, count, link) —
 * never DLQ item payloads.
 */
export function taskeiCreateUrl(
  stage: Stage,
  investigation: Investigation,
  roomId: string,
  parentTaskId?: string
): string {
  const investigationUrl = `${window.location.origin}${investigationPath(
    investigation.id,
    stage
  )}`;
  const ruleLines = (investigation.rule || []).map((p) => {
    const label =
      RULE_FIELDS.find((f) => f.field === p.field)?.label || p.field;
    if (isExclusion(p)) {
      return `- ${label}: none of ${excludedValues(p)
        .map((v) => `\`${v}\``)
        .join(", ")}`;
    }
    return `- ${label}: ${predicateValues(p)
      .map((v) => `\`${v}\``)
      .join(" or ")}`;
  });
  const description = [
    `**Lusca Search investigation:** ${investigationUrl}`,
    `**Stage:** ${stage}`,
    `**Population:** ${investigation.population?.count ?? 0} DLQ items`,
    ...(ruleLines.length ? ["", "**Rule**", ...ruleLines] : []),
    ...(investigation.description
      ? ["", "**Notes**", investigation.description]
      : []),
    "",
    "_Once created, paste this task's URL into the investigation with “Link task”._",
  ].join("\n");
  const params = new URLSearchParams({
    room: roomId,
    name: `[DLQ ${stage}] ${investigation.title}`,
    description,
    descriptionContentType: "SIM_MARKDOWN",
    type: "TASK",
    tags: `lusca-investigation-${investigation.id}`,
  });
  if (investigation.owner) {
    params.set("assignee", `kerberos:${investigation.owner}@ANT.AMAZON.COM`);
  }
  if (parentTaskId) params.set("parentTask", parentTaskId);
  return `${TASKEI_CREATE_URL}?${params.toString()}`;
}

/**
 * The tracking Taskei task for an investigation. Unlinked: "Create task" opens Taskei with the
 * form prefilled (room from server config, assignee = owner), and "Link task" attaches an existing
 * task by URL. Linked: a button to the task, with unlink for the linker or the owner.
 */
export const InvestigationTaskAction: React.FC<
  InvestigationTaskActionProps
> = ({ stage, investigation, currentUser, onError }) => {
  const update = useUpdateInvestigation(stage);
  // "link": the user chose to attach an existing task. "afterCreate": they just clicked
  // Create task, so Taskei is open in another tab and this dialog waits for the new task's URL.
  const [linking, setLinking] = useState<false | "link" | "afterCreate">(false);
  const [draft, setDraft] = useState("");
  const inputRef = useRef<InputProps.Ref>(null);
  const {
    task,
    taskeiRoomId: roomId,
    taskeiParentTaskId: parentTaskId,
    taskeiSyncEnabled,
    taskSync,
  } = investigation;
  const queryClient = useQueryClient();
  // Automatic sync: the worker creates the task a few seconds after the investigation is saved.
  // Poll the detail query while we wait so the chip appears without a manual refresh.
  const awaitingAutoTask =
    !!taskeiSyncEnabled &&
    !task &&
    !taskSync &&
    investigation.status !== "RESOLVED";
  useEffect(() => {
    if (!awaitingAutoTask) return undefined;
    const timer = window.setInterval(() => {
      queryClient
        .invalidateQueries(investigationKeys.detail(stage, investigation.id))
        .catch(() => undefined);
    }, 4000);
    return () => window.clearInterval(timer);
  }, [awaitingAutoTask, queryClient, stage, investigation.id]);
  const isOwner = !!currentUser && investigation.owner === currentUser;
  const mayUnlink = isOwner || (!!task && task.linkedBy === currentUser);

  useEffect(() => {
    if (!linking) setDraft("");
  }, [linking]);

  // Coming back from the Taskei tab: put the cursor in the box so a paste lands straight in it.
  useEffect(() => {
    if (!linking) return undefined;
    const refocus = () => inputRef.current?.focus();
    window.addEventListener("focus", refocus);
    return () => window.removeEventListener("focus", refocus);
  }, [linking]);

  const run = (
    request: Omit<InvestigationUpdateRequest, "expectedVersion">
  ): void => {
    update
      .mutateAsync({
        investigationId: investigation.id,
        request: { expectedVersion: investigation.version, ...request },
      })
      .then(() => setLinking(false))
      .catch((err: InvestigationApiError) => onError(saveErrorText(err)));
  };

  const trimmed = draft.trim();
  const valid = !!trimmed && isTaskReference(trimmed);
  let dialogHeader = "Link a Taskei task";
  if (linking === "afterCreate")
    dialogHeader = "Link the task you just created";
  else if (task) dialogHeader = "Replace linked task";

  const linkDialog = linking && (
    <Modal
      visible
      size="medium"
      onDismiss={() => setLinking(false)}
      header={dialogHeader}
      footer={
        <Box float="right">
          <SpaceBetween direction="horizontal" size="xs">
            <Button
              onClick={() => setLinking(false)}
              disabled={update.isLoading}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={!valid}
              loading={update.isLoading}
              onClick={() => run({ linkTask: { url: trimmed } })}
            >
              Link
            </Button>
          </SpaceBetween>
        </Box>
      }
    >
      <FormField
        label="Task URL or id"
        description={
          linking === "afterCreate"
            ? "Taskei opened in a new tab with the form prefilled. Once you have created the task there, copy its URL (or id) and paste it here."
            : "Paste the Taskei task URL (https://taskei.amazon.dev/tasks/…), a SIM issue URL, or a task id such as LUSCA-123."
        }
        errorText={
          trimmed && !valid
            ? "That does not look like a Taskei task URL or id."
            : undefined
        }
      >
        <Input
          ref={inputRef}
          value={draft}
          onChange={({ detail }) => setDraft(detail.value)}
          onKeyDown={({ detail }) => {
            if (detail.key === "Enter" && valid)
              run({ linkTask: { url: trimmed } });
          }}
          placeholder="https://taskei.amazon.dev/tasks/LUSCA-123"
          autoFocus
          ariaLabel="Task URL or id"
        />
      </FormField>
    </Modal>
  );

  if (task && !mayUnlink) {
    // Nothing the caller may do with someone else's link: just the task itself, no menu.
    return (
      <Button
        iconName="ticket"
        href={safeExternalHref(task.url)}
        target="_blank"
        ariaLabel={`Open task ${task.id} in Taskei`}
      >
        {task.id}
      </Button>
    );
  }

  if (task) {
    return (
      <>
        <ButtonDropdown
          mainAction={{
            text: task.id,
            iconName: "ticket",
            href: safeExternalHref(task.url),
            target: "_blank",
            ariaLabel: `Open task ${task.id} in Taskei`,
          }}
          ariaLabel="Task actions"
          items={[
            {
              id: "replace",
              text: "Replace linked task…",
            },
            {
              id: "unlink",
              text: "Unlink task",
              description: taskeiSyncEnabled
                ? "Stops mirroring; no new task is created"
                : undefined,
            },
          ]}
          onItemClick={({ detail }) => {
            if (detail.id === "replace") setLinking("link");
            if (detail.id === "unlink") run({ unlinkTask: true });
          }}
        />
        {linkDialog}
      </>
    );
  }

  if (awaitingAutoTask) {
    return (
      <>
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <StatusIndicator type="loading">
            Creating Taskei task…
          </StatusIndicator>
          <ButtonDropdown
            variant="icon"
            ariaLabel="Task actions"
            items={[{ id: "link", text: "Link existing task…" }]}
            onItemClick={({ detail }) => {
              if (detail.id === "link") setLinking("link");
            }}
          />
        </SpaceBetween>
        {linkDialog}
      </>
    );
  }

  if (!roomId) {
    // No room configured for this deployment: linking an existing task is still possible.
    return (
      <>
        <Button iconName="ticket" onClick={() => setLinking("link")}>
          Link task
        </Button>
        {linkDialog}
      </>
    );
  }

  // Manual path: sync is off for this deployment, or it tried and could not create the task.
  const createButton = (
    <ButtonDropdown
      mainAction={{
        text: "Create task",
        iconName: "ticket",
        href: taskeiCreateUrl(stage, investigation, roomId, parentTaskId),
        target: "_blank",
        ariaLabel: "Create a Taskei task for this investigation (opens Taskei)",
        // The link still opens Taskei; meanwhile the dialog waits here for the new URL.
        onFollow: () => setLinking("afterCreate"),
      }}
      ariaLabel="Task actions"
      items={[{ id: "link", text: "Link existing task…" }]}
      onItemClick={({ detail }) => {
        if (detail.id === "link") setLinking("link");
      }}
    />
  );
  return (
    <>
      {taskSync && taskSync.status !== "UNLINKED" ? (
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Popover
            dismissButton={false}
            size="medium"
            header={
              taskSync.status === "DEFERRED"
                ? "Automatic task creation is waiting on access"
                : "Automatic task creation failed"
            }
            content={taskSync.error || "No details recorded."}
          >
            <StatusIndicator
              type={taskSync.status === "DEFERRED" ? "pending" : "warning"}
            >
              {taskSync.status === "DEFERRED"
                ? "Task sync deferred"
                : "Task sync failed"}
            </StatusIndicator>
          </Popover>
          {createButton}
        </SpaceBetween>
      ) : (
        createButton
      )}
      {linkDialog}
    </>
  );
};

export default InvestigationTaskAction;

/**
 * Shown when the worker found the Taskei task disagreeing with the investigation (someone edited
 * the task directly). Lusca stays the source of truth, so the owner picks a side: apply the task's
 * change here, or push Lusca's owner/status back onto the task.
 */
export const InvestigationTaskDriftBanner: React.FC<{
  stage: Stage;
  investigation: Investigation;
  currentUser?: string | null;
  onError: (message: string) => void;
}> = ({ stage, investigation, currentUser, onError }) => {
  const update = useUpdateInvestigation(stage);
  const transfer = useTransferInvestigation(stage);
  const claim = useClaimInvestigation(stage);
  const release = useReleaseInvestigation(stage);
  const { task, taskDrift: drift, owner } = investigation;
  if (!task || !drift) return null;
  const isOwner = !!currentUser && owner === currentUser;
  const mayAct = isOwner || !owner;
  const busy =
    update.isLoading ||
    transfer.isLoading ||
    claim.isLoading ||
    release.isLoading;
  const versioned = { investigationId: investigation.id };
  const guard = (promise: Promise<unknown>) => {
    promise.catch((error: unknown) =>
      onError(
        error instanceof Error && error.message
          ? error.message
          : "The task could not be reconciled. Try again."
      )
    );
  };
  const taskClosed = drift.taskStatus === "closed";
  const investigationClosed = investigation.status === "RESOLVED";

  const differences: string[] = [];
  if (drift.assigneeDiffers) {
    differences.push(
      drift.taskAssignee
        ? `the task is assigned to ${drift.taskAssignee}${
            owner
              ? ` but ${owner} owns the investigation`
              : " but nobody owns the investigation"
          }`
        : `the task is unassigned${
            owner ? ` but ${owner} owns the investigation` : ""
          }`
    );
  }
  if (drift.statusDiffers) {
    differences.push(
      taskClosed
        ? "the task is resolved but the investigation is still open"
        : "the task was reopened but the investigation is resolved"
    );
  }

  // "Apply the task's change to Lusca" — only for the pieces that can be expressed as an
  // ordinary investigation action. Status is deliberately not auto-resolved: resolving needs a
  // reason, which the resolve dialog collects.
  const applyAssignee = () => {
    if (drift.taskAssignee && owner) {
      guard(
        transfer.mutateAsync({
          ...versioned,
          request: {
            expectedVersion: investigation.version,
            newOwner: drift.taskAssignee,
          },
        })
      );
    } else if (
      drift.taskAssignee &&
      !owner &&
      drift.taskAssignee === currentUser
    ) {
      guard(
        claim.mutateAsync({
          ...versioned,
          expectedVersion: investigation.version,
        })
      );
    } else if (!drift.taskAssignee && owner) {
      guard(
        release.mutateAsync({
          ...versioned,
          expectedVersion: investigation.version,
        })
      );
    }
  };
  const canApplyAssignee =
    drift.assigneeDiffers &&
    mayAct &&
    (!!owner || drift.taskAssignee === currentUser);
  let applyAssigneeLabel = "Release ownership";
  if (drift.taskAssignee) {
    applyAssigneeLabel = owner
      ? `Transfer to ${drift.taskAssignee}`
      : "Claim investigation";
  }

  const pushBack = () =>
    guard(
      update.mutateAsync({
        ...versioned,
        request: {
          expectedVersion: investigation.version,
          pushTaskState: true,
        },
      })
    );

  return (
    <Alert
      type="warning"
      header={`Task ${task.id} differs from this investigation`}
      data-testid="task-drift-banner"
      action={
        <SpaceBetween direction="horizontal" size="xs">
          {canApplyAssignee && (
            <Button
              onClick={applyAssignee}
              disabled={busy}
              loading={
                transfer.isLoading || claim.isLoading || release.isLoading
              }
            >
              {applyAssigneeLabel}
            </Button>
          )}
          <Button
            onClick={pushBack}
            disabled={!isOwner || busy}
            loading={update.isLoading}
          >
            Update task to match
          </Button>
        </SpaceBetween>
      }
    >
      Someone changed it in Taskei: {differences.join("; ")}.{" "}
      {drift.statusDiffers && !investigationClosed && taskClosed
        ? "Resolve the investigation from the Status panel if the work is really done, or update the task to reopen it."
        : null}
      {drift.statusDiffers && investigationClosed && !taskClosed
        ? "Reopen the investigation from the Status panel if there is more to do, or update the task to resolve it again."
        : null}
    </Alert>
  );
};

/** Small inline link for tables/drawers: "LUSCA-123" or nothing. */
export const InvestigationTaskLink: React.FC<{
  investigation: Investigation;
}> = ({ investigation }) =>
  investigation.task ? (
    <SafeExternalLink href={investigation.task.url}>
      {investigation.task.id}
    </SafeExternalLink>
  ) : null;
